import { useEffect, useState } from "react";
import { Star, Trash2, Volume2, Sparkles, SpellCheck2 } from "lucide-react";
import { SpellGame } from "@/components/words/SpellGame";
import { WordStars } from "@/components/parent/KidWords";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { speak } from "@/lib/buddy";
import { WordQuiz } from "@/components/words/WordQuiz";
import { SpanishWords } from "@/components/words/SpanishWords";

const WordCard = ({ w, voice, onRemove }) => (
  <article className="rounded-3xl bg-white/80 border border-[#E6DEC3] p-5 rise" data-testid={`word-card-${w.id}`}>
    <div className="flex items-start gap-2">
      <p className="font-display text-2xl text-[#2C4A3E] flex-1">{w.syllables}</p>
      <WordStars w={w} />
      <button onClick={() => speak(`${w.word}. ${w.meaning}`, voice, 0.8).catch(() => {})} data-testid={`word-say-${w.id}`} aria-label="Hear it" className="p-2 rounded-full bg-[#F1EADB]"><Volume2 className="w-4 h-4" /></button>
      <button onClick={() => onRemove(w)} data-testid={`word-remove-${w.id}`} aria-label="Remove" className="p-2 rounded-full hover:bg-[#F1EADB] text-stone-500"><Trash2 className="w-4 h-4" /></button>
    </div>
    <p className="text-stone-700 mt-2">{w.meaning}</p>
    {w.example && <p className="text-sm text-stone-500 italic mt-2">“{w.example}”</p>}
    <p className="text-xs text-stone-500 mt-3">{w.mastered ? "Meaning star earned" : `Meaning: ${w.streak}/3 in a row`} · {w.spell_mastered ? "Spelling star earned" : `Spelling: ${w.spell_streak || 0}/2 in a row`}</p>
  </article>
);

export default function MyWords() {
  const { buddy } = useProfile();
  const [words, setWords] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [tab, setTab] = useState("en");
  const load = () => http.get("/words/me").then((r) => setWords(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (w) => {
    try { await http.delete(`/words/me/${w.id}`); setWords((ws) => ws.filter((x) => x.id !== w.id)); toast.success(`Removed "${w.word}"`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const stars = words?.filter((w) => w.mastered).length || 0;
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 pb-40" data-testid="my-words-page">
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Word Bank</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] mt-3">My Words</h1>
      <p className="mt-4 text-stone-600 max-w-2xl" data-testid="my-words-subtitle">{tab === "es" ? "Every word you tap with Español on is saved here. Flip the cards, hear them in Spanish, and earn a Spanish star when you get one right 3 times in a row." : <>Every word you long-press while reading is saved here. Practice with {buddy?.name || "your Buddy"} and get a star when you know it 3 times in a row.</>}</p>
      <div className="mt-8 inline-flex gap-1 p-1 rounded-full bg-[#F1EADB]" role="tablist" data-testid="my-words-tabs">
        {[["en", "English words"], ["es", "Spanish"]].map(([v, l]) => <button key={v} role="tab" aria-selected={tab === v} onClick={() => { setTab(v); setQuiz(null); }} data-testid={`my-words-tab-${v}`}
          className={`px-5 py-2 rounded-full text-sm transition-colors ${tab === v ? "bg-[#2C4A3E] text-white" : "text-[#2C4A3E] hover:bg-white/70"}`}>{l}</button>)}
      </div>
      {tab === "es" ? <div className="mt-8"><SpanishWords /></div> : <>
      {words?.length > 0 && !quiz && (
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button onClick={() => setQuiz("meaning")} data-testid="word-quiz-start" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C87D53] text-white"><Sparkles className="w-4 h-4" /> Meaning quiz with {buddy?.name || "my Buddy"}</button>
          <button onClick={() => setQuiz("spell")} data-testid="spell-game-start" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4A3E] text-white"><SpellCheck2 className="w-4 h-4" /> Spelling game</button>
          <span className="text-sm text-stone-600" data-testid="my-words-count">{words.length} words · <Star className="inline w-4 h-4 text-[#D99B26] fill-current" /> {stars} meanings · <SpellCheck2 className="inline w-4 h-4 text-[#6B9080]" /> {words.filter((w) => w.spell_mastered).length} spelled</span>
        </div>
      )}
      <div className="mt-8">
        {quiz === "spell" ? <SpellGame onExit={() => { setQuiz(null); load(); }} /> : quiz ? <WordQuiz onExit={() => { setQuiz(null); load(); }} /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-words-list">
            {words?.map((w) => <WordCard key={w.id} w={w} voice={buddy?.voice} onRemove={remove} />)}
          </div>
        )}
        {words && !words.length && <p className="text-stone-500" data-testid="my-words-empty">No words yet. While reading a lesson, press and hold any word to hear it slowly and save it here.</p>}
        {!words && <p className="text-stone-500">Loading…</p>}
      </div>
      </>}
    </div>
  );
}
